/**
 * §P.12 · La biblioteca técnica del club.
 *
 * ## Se llama `tiposDelTemario.ts` y no `temario.ts`
 *
 * Por lo mismo que `tiposDeExamen.ts`: macOS no distingue mayúsculas en los nombres
 * de fichero, así que `temario.ts` y `Temario.tsx` son el mismo nombre para el
 * sistema de ficheros y el segundo pisa al primero. En Linux —la CI— no pasa, que es
 * la peor forma de que pase.
 *
 * ## Kiap no aloja vídeo, y eso es una decisión, no una carencia
 *
 * `external_url` lleva `CHECK (external_url ~ '^https://')` en la columna. El club
 * ya tiene el vídeo publicado en su canal o en la web de su federación; guardarlo
 * otra vez sería pagar el almacenamiento, el ancho de banda y la responsabilidad a
 * cambio de nada. La pantalla lo dice con esas palabras, porque si no la primera
 * pregunta del maestro es dónde se sube el archivo.
 *
 * ## `academy_id: null` es temario global
 *
 * Lo comparten todos los clubes que siguen ese sistema de grados. El servidor manda
 * `is_mine` ya calculado y **eso es lo que decide si salen los botones**: editar uno
 * global cambiaría el temario de los otros ciento noventa y nueve clubes, así que el
 * `PATCH` responde `409 global_curriculum_read_only`. Para el alumno los dos son lo
 * mismo —lo que tiene que saberse—, y por eso salen en la misma lista y no en dos.
 */

export interface TecnicaDelTemario {
  readonly id: string;
  /** `null` = global. Se lee por `is_mine`, que ya viene resuelto. */
  readonly academy_id: string | null;
  /** Lo que decide si salen los botones de editar. */
  readonly is_mine: boolean;
  readonly belt_rank_id: string;
  readonly rank_name: string;
  readonly rank_sort_order: number;
  readonly title: string;
  readonly description: string | null;
  /** Siempre `https://`. Ver la cabecera. */
  readonly external_url: string | null;
  readonly category: string | null;
  readonly sort_order: number;
  readonly created_at: Date | string;
}

/** Un cinturón con sus técnicas, que es como se lee un temario. */
export interface BloqueDeTemario {
  readonly belt_rank_id: string;
  readonly rank_name: string;
  readonly tecnicas: readonly TecnicaDelTemario[];
}

/**
 * Agrupa por cinturón **conservando el orden del servidor**.
 *
 * El servidor ya devuelve la lista ordenada por grado y, dentro del grado, por su
 * orden: es como se estudia un temario, de abajo arriba y en el orden en que se
 * enseña. Alfabéticamente sería una lista correcta e inútil.
 *
 * Lo que aquí no se hace es reordenar: se recorre y se va abriendo bloque cuando
 * cambia el cinturón. **Y se usa un `Map` y no «¿cambió respecto al anterior?»**,
 * porque un club con dos disciplinas tiene dos escaleras y sus `rank_sort_order`
 * se entrelazan: el amarillo de taekwondo y el de hapkido pueden salir separados
 * por un cinturón de la otra, y comparando con el anterior el amarillo aparecería
 * dos veces.
 */
export function porCinturon(
  tecnicas: readonly TecnicaDelTemario[]
): BloqueDeTemario[] {
  const bloques = new Map<string, TecnicaDelTemario[]>();
  const nombres = new Map<string, string>();

  for (const t of tecnicas) {
    const suyas = bloques.get(t.belt_rank_id);
    if (suyas) {
      suyas.push(t);
    } else {
      bloques.set(t.belt_rank_id, [t]);
      nombres.set(t.belt_rank_id, t.rank_name);
    }
  }

  return [...bloques].map(([belt_rank_id, tecnicas]) => ({
    belt_rank_id,
    rank_name: nombres.get(belt_rank_id) ?? '',
    tecnicas,
  }));
}

/**
 * Las categorías que hay puestas, para el filtro.
 *
 * `category` es texto libre y opcional —«pumsae», «defensa personal», «teoría»—, así
 * que las opciones no se pueden escribir de antemano: **salen de lo que el club haya
 * usado**. Es lo contrario de un enum, y por eso no está en ningún sitio como lista.
 *
 * Ordenadas alfabéticamente, que aquí sí: un filtro es una lista para buscar en ella,
 * no un temario que se estudia en orden.
 */
export function categoriasUsadas(
  tecnicas: readonly TecnicaDelTemario[]
): string[] {
  const vistas = new Set<string>();
  for (const t of tecnicas) if (t.category) vistas.add(t.category);
  return [...vistas].sort((a, b) => a.localeCompare(b, 'es'));
}

/**
 * El siguiente hueco libre dentro de un cinturón.
 *
 * Una técnica nueva se añade **al final de su cinturón**, que es lo que espera quien
 * está escribiendo el temario de arriba abajo. Pedirle el número a mano sería pedirle
 * que lleve la cuenta él.
 *
 * Reordenar sí es a mano, en el campo «Orden» de la edición: no hay una ruta que
 * mueva varias de una vez —`PATCH` va de una en una— y arrastrar con el ratón
 * dispararía una petición por fila y una carrera entre ellas. Cuando haga falta de
 * verdad se pide la ruta en `endpoints.md`, que es donde se diseñan.
 */
export function siguienteOrden(
  tecnicas: readonly TecnicaDelTemario[],
  beltRankId: string
): number {
  const delCinturon = tecnicas.filter((t) => t.belt_rank_id === beltRankId);
  if (delCinturon.length === 0) return 0;
  return Math.max(...delCinturon.map((t) => t.sort_order)) + 1;
}

/**
 * ¿Es un enlace que el servidor va a aceptar?
 *
 * El `CHECK` de la columna exige `^https://` y el DTO repite el patrón, así que un
 * `http://` pegado de una web vieja se rechaza con un `422` que habla de un patrón.
 * Vale más decirlo antes de enviar, y decir **por qué**: no es purismo, es que el
 * navegador bloquea el contenido inseguro dentro de una página servida por HTTPS y
 * el enlace no abriría.
 *
 * Cadena vacía es válida: significa «sin enlace», que es el caso normal.
 */
export function enlaceValido(texto: string): boolean {
  const limpio = texto.trim();
  return limpio === '' || limpio.startsWith('https://');
}
